import { GoogleGenerativeAI } from "@google/generative-ai";
import * as XLSX from "xlsx";

// Initialize the Gemini AI
const genAI = new GoogleGenerativeAI(
  process.env.REACT_APP_VISURE_DETAILS_GEMINI_API_KEY,
);

/**
 * Converts a file to base64 string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Converts Excel/CSV to text format using xlsx library
 */
const convertExcelToText = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to CSV format (preserves structure better than JSON)
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        
        console.log("Excel convertito in CSV:", csv);
        resolve(csv);
      } catch (error) {
        console.error("Errore conversione Excel:", error);
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Analyzes tariff documents using Gemini Vision API
 * Extracts key tariff information
 */
export const analyzeTariffDocument = async (file) => {
  try {
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

    let prompt = `Analizza questo documento di tariffa energetica (Luce o Gas) ed estrai le informazioni in formato JSON.

IMPORTANTE: Se ci sono più tariffe nel documento, restituisci un ARRAY di oggetti JSON. Se c'è una sola tariffa, restituisci comunque un array con un solo elemento.

SCHEMA JSON DA RESTITUIRE (per ogni tariffa nell'array):
{
  "fornitore": "nome esatto del fornitore energetico",
  "nomeOfferta": "nome commerciale dell'offerta",
  "tipo": "luce" | "gas" | "dual",
  "tipoPrezzo": "fisso" | "variabile" | "indicizzato",
  "durataContratto": "durata in mesi (numero)",
  
  "prezzoMateriaPrima": {
    "valore": "prezzo unitario (numero decimale)",
    "unita": "€/kWh" | "€/Smc",
    "fasceOrarie": true | false
  },
  
  "spread": "spread sul PUN o PSV (numero decimale)",
  
  "pcv": {
    "valoreMensile": "quota fissa mensile (numero)",
    "valoreAnnuo": "quota fissa annua (numero)"
  },
  
  "corrispettiviTrasporto": {
    "valoreMensile": "costo trasporto mensile (numero)",
    "valoreAnnuo": "costo trasporto annuo (numero)"
  },
  
  "oneriSistema": "descrizione oneri di sistema",
  "imposte": "descrizione imposte applicate",
  
  "dataValidita": {
    "inizio": "data inizio validità YYYY-MM-DD",
    "fine": "data fine validità YYYY-MM-DD"
  },
  
  "note": "eventuali note, clausole o condizioni particolari",
  "confidenceScore": "livello di confidenza estrazione 0-100 (numero)",
  "needsReview": "true se dati incerti, false altrimenti (boolean)"
}

ISTRUZIONI:
1. Restituisci SOLO il JSON, nessun testo aggiuntivo
2. Se un campo non è presente, usa null
3. Usa valori numerici per i prezzi, non stringhe
4. Per confidenceScore, valuta accuratamente:
   - 95-100: Tutti i campi presenti e chiari, nessuna ambiguità
   - 85-94: Maggior parte dei campi presenti, alcuni potrebbero richiedere verifica
   - 70-84: Dati parziali ma utilizzabili, diversi campi mancanti
   - 50-69: Molti campi mancanti o ambigui
   - <50: Dati molto incerti o quasi tutti mancanti
5. Imposta needsReview=true se confidenceScore < 85 o dati dubbi
6. Riconosci fornitori: Enel, Plenitude, Iren, Edison, Acea, Sorgenia, A2A, etc.
7. Per tipo: "luce" per elettricità, "gas" per metano, "dual" per offerte combinate
8. IMPORTANTE: "spread" deve essere un numero semplice, non un oggetto
9. Sii generoso con il confidence score se i dati chiave (fornitore, offerta, tipo, prezzo) sono chiari

ESEMPIO DI OUTPUT VALIDO:
{
  "fornitore": "Enel Energia",
  "nomeOfferta": "Sempre Con Te Special",
  "tipo": "luce",
  "prezzoMateriaPrima": {
    "valore": 0.085,
    "unita": "€/kWh",
    "fasceOrarie": true
  },
  "spread": 0.012,
  "pcv": {
    "valoreMensile": 45.5,
    "valoreAnnuo": 546.0
  },
  "confidenceScore": 85,
  "needsReview": false
}

Analizza attentamente e estrai tutti i dati disponibili.`;

    let parts = [];

    // Handle different file types
    if (file.type === "text/csv" || file.name.endsWith(".csv") ||
        file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      // For Excel/CSV, convert to text first
      const textContent = await convertExcelToText(file);
      parts = [
        { text: prompt },
        { text: `\n\nDATI FILE:\n${textContent}` }
      ];
    } else if (file.type.startsWith("image/") || file.type === "application/pdf") {
      // For images and PDFs, use base64
      const base64Data = await fileToBase64(file);
      const mimeType = file.type || "image/jpeg";
      
      parts = [
        { text: prompt },
        {
          inlineData: {
            mimeType: mimeType,
            data: base64Data,
          },
        },
      ];
    } else {
      throw new Error("Tipo di file non supportato");
    }

    const result = await model.generateContent(parts);
    const response = await result.response;
    const text = response.text();

    console.log("Risposta AI grezza:", text);

    // Parse JSON response - try to extract JSON from markdown code blocks or plain text
    let jsonText = text;
    
    // Remove markdown code blocks if present
    if (text.includes("```json")) {
      jsonText = text.match(/```json\s*([\s\S]*?)\s*```/)?.[1] || text;
    } else if (text.includes("```")) {
      jsonText = text.match(/```\s*([\s\S]*?)\s*```/)?.[1] || text;
    }
    
    // Extract JSON object or array - find first { or [ and last } or ]
    const firstBrace = jsonText.indexOf('{');
    const firstBracket = jsonText.indexOf('[');
    const lastBrace = jsonText.lastIndexOf('}');
    const lastBracket = jsonText.lastIndexOf(']');
    
    let jsonString;
    let isArray = false;
    
    // Determine if it's an array or object
    if (firstBracket !== -1 && (firstBrace === -1 || firstBracket < firstBrace)) {
      // It's an array
      isArray = true;
      jsonString = jsonText.substring(firstBracket, lastBracket + 1);
    } else if (firstBrace !== -1) {
      // It's an object
      jsonString = jsonText.substring(firstBrace, lastBrace + 1);
    } else {
      console.error("Nessun JSON trovato nella risposta");
      throw new Error("Impossibile estrarre JSON dalla risposta AI");
    }
    
    console.log("JSON estratto:", jsonString.substring(0, 500) + "...");

    const parsedData = JSON.parse(jsonString);
    
    // Ensure we always work with an array
    let dataArray;
    if (Array.isArray(parsedData)) {
      dataArray = parsedData;
      console.log(`Array di ${dataArray.length} tariffe rilevato.`);
    } else {
      // Single object, wrap in array
      dataArray = [parsedData];
      console.log("Singola tariffa rilevata, convertita in array.");
    }
    
    if (dataArray.length === 0) {
      throw new Error("Nessuna tariffa estratta dal documento");
    }
    
    // Validate and fix essential fields for each tariff
    dataArray = dataArray.map((tariff, index) => {
      console.log(`Tariffa ${index + 1}:`, tariff);
      
      if (!tariff.fornitore || !tariff.nomeOfferta || !tariff.tipo) {
        console.warn(`Tariffa ${index + 1}: Campi essenziali mancanti, aggiungo valori di default`);
        tariff.fornitore = tariff.fornitore || "Fornitore Sconosciuto";
        tariff.nomeOfferta = tariff.nomeOfferta || `Offerta da Verificare ${index + 1}`;
        tariff.tipo = tariff.tipo || "luce";
        tariff.needsReview = true;
        // Abbassa il confidence score se mancano campi essenziali, ma non troppo
        tariff.confidenceScore = Math.max((tariff.confidenceScore || 50) - 20, 30);
      }
      
      // Assicura che il confidence score sia nel range corretto
      tariff.confidenceScore = Math.min(Math.max(tariff.confidenceScore || 70, 0), 100);
      
      return tariff;
    });

    return {
      success: true,
      data: dataArray,
      count: dataArray.length,
    };
  } catch (error) {
    console.error("Errore analisi tariffa:", error);
    return {
      success: false,
      error: error.message || "Errore durante l'analisi del documento",
      data: null,
    };
  }
};

/**
 * Validates tariff data according to business rules
 */
export const validateTariffData = (tariff) => {
  const errors = [];
  const warnings = [];

  // Validate fornitore
  if (!tariff.fornitore || tariff.fornitore.trim() === "") {
    errors.push("Fornitore mancante");
  }

  // Validate nomeOfferta
  if (!tariff.nomeOfferta || tariff.nomeOfferta.trim() === "") {
    errors.push("Nome offerta mancante");
  }

  // Validate tipo
  if (!["luce", "gas", "dual"].includes(tariff.tipo)) {
    errors.push("Tipo non valido (deve essere luce, gas o dual)");
  }

  // Validate prezzoMateriaPrima
  if (tariff.prezzoMateriaPrima) {
    const prezzo = tariff.prezzoMateriaPrima.valore;
    if (prezzo === 0 || prezzo === null) {
      errors.push("Prezzo materia prima uguale a zero o mancante");
    }
    if (prezzo < 0) {
      errors.push("Prezzo materia prima negativo");
    }
    if (!tariff.prezzoMateriaPrima.unita) {
      errors.push("Unità di misura mancante per prezzo materia prima");
    }
  } else {
    errors.push("Prezzo materia prima mancante");
  }

  // Validate PCV
  if (tariff.pcv) {
    const pcvAnnuo = tariff.pcv.valoreAnnuo;
    if (pcvAnnuo !== null && pcvAnnuo !== undefined) {
      if (pcvAnnuo < 40) {
        warnings.push("PCV annuo sospetto: troppo basso (< 40€)");
      }
      if (pcvAnnuo > 300) {
        warnings.push("PCV annuo sospetto: troppo alto (> 300€)");
      }
    }
  }

  // Validate confidence score
  if (tariff.confidenceScore < 70) {
    warnings.push("Confidence score basso: richiede revisione");
  }

  // Set needsReview flag
  const needsReview = errors.length > 0 || 
                      warnings.length > 0 || 
                      tariff.confidenceScore < 80 ||
                      tariff.needsReview === true;

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    needsReview,
  };
};
